import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { useDiscount } from "../contexts/DiscountContext";
import { toast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { ShieldCheck, Plus, Trash2, Tag, Pencil, Check, X } from "lucide-react";

const AdminDiscountPanel = () => {
  const {
    discountCodes,
    loading,
    addDiscountCode,
    removeDiscountCode,
    toggleDiscountCode,
    updateDiscountCode,
  } = useDiscount();
  const [newCode, setNewCode] = useState("");
  const [newPercentage, setNewPercentage] = useState("");
  const [newOrg, setNewOrg] = useState("");
  const [newPlans, setNewPlans] = useState(["Core", "Pro"]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPercentage, setEditPercentage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const handleAdd = () => {
    if (
      !newCode.trim() ||
      !newPercentage ||
      !newOrg.trim() ||
      newPlans.length === 0
    ) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }
    const pct = Number(newPercentage);
    if (pct <= 0 || pct > 100) {
      toast({
        title: "Invalid percentage",
        description: "Percentage must be between 1 and 100.",
        variant: "destructive",
      });
      return;
    }
    addDiscountCode({
      code: newCode.toUpperCase().trim(),
      percentage: pct,
      organization: newOrg.trim(),
      plans: newPlans,
      active: true,
    });
    setNewCode("");
    setNewPercentage("");
    setNewOrg("");
    setNewPlans(["Core", "Pro"]);
    setShowForm(false);
    toast({
      title: "Discount code created",
      description: `Code "${newCode.toUpperCase()}" has been added.`,
    });
  };

  const handleRemove = (id, code) => {
    setConfirmTarget({ id, code });
    setConfirmOpen(true);
  };

  const startEditing = (id, currentPercentage) => {
    setEditingId(id);
    setEditPercentage(String(currentPercentage));
  };

  const saveEdit = (id) => {
    const pct = Number(editPercentage);
    if (pct <= 0 || pct > 100) {
      toast({
        title: "Invalid percentage",
        description: "Must be between 1 and 100.",
        variant: "destructive",
      });
      return;
    }
    updateDiscountCode(id, { percentage: pct });
    setEditingId(null);
    toast({
      title: "Discount updated",
      description: `Percentage updated to ${pct}%.`,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditPercentage("");
  };
  const togglePlan = (plan) => {
    setNewPlans((prev) =>
      prev.includes(plan) ? prev.filter((p) => p !== plan) : [...prev, plan],
    );
  };

  return (
    <Card className="shadow-[var(--card-shadow)]">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-accent" />
          <CardTitle className="text-lg font-display">Discount Codes</CardTitle>
        </div>
        <CardDescription>
          Create and manage discount codes for organizations. Discounts apply to
          subscription pricing.
        </CardDescription>
        {loading && (
          <p className="text-xs text-muted-foreground">Loading discounts…</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing Codes */}
        {discountCodes.length > 0 ? (
          <div className="space-y-2">
            {discountCodes.map((dc) => (
              <div
                key={dc.discountId}
                className="flex items-center justify-between rounded-md border px-3 py-2.5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono font-semibold">
                        {dc.code}
                      </span>
                      {editingId === dc.discountId ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            value={editPercentage}
                  onChange={(e) => setEditPercentage(e.target.value)}
                  className="h-6 w-16 text-xs px-1.5"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(dc.discountId);
                              if (e.key === "Escape") cancelEdit();
                            }}
                          />
                          <span className="text-xs text-muted-foreground">
                            %
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => saveEdit(dc.discountId)}
                          >
                            <Check className="h-3 w-3 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={cancelEdit}
                          >
                            <X className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        </div>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-[10px] cursor-pointer hover:bg-secondary/80"
                          onClick={() =>
                            startEditing(dc.discountId, dc.percentage)
                          }
                        >
                          {dc.percentage}% off{" "}
                          <Pencil className="h-2.5 w-2.5 ml-1" />
                        </Badge>
                      )}
                      {dc.plans.map((p) => (
                        <Badge
                          key={p}
                          variant="outline"
                          className="text-[10px]"
                        >
                          {p}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {dc.organization}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={dc.active}
                    disabled={loading}
                    onCheckedChange={() => toggleDiscountCode(dc.discountId)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(dc.discountId, dc.code)}
                    disabled={loading}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No discount codes yet.
          </p>
        )}

        <Separator />

        {/* Add New Code */}
        {showForm ? (
          <div className="space-y-3 rounded-lg border border-dashed p-4">
            <p className="text-sm font-medium">New Discount Code</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Code</Label>
                <Input
                  placeholder="e.g. SAVE25"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  className="text-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Discount %</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="e.g. 25"
                  value={newPercentage}
                  onChange={(e) => setNewPercentage(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Organization</Label>
                <Input
                  placeholder="e.g. Acme Corp"
                  value={newOrg}
                  onChange={(e) => setNewOrg(e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Applicable Plans</Label>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="plan-core"
                    checked={newPlans.includes("Core")}
                    onCheckedChange={() => togglePlan("Core")}
                  />
                  <Label htmlFor="plan-core" className="text-sm">
                    Core
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="plan-pro"
                    checked={newPlans.includes("Pro")}
                    onCheckedChange={() => togglePlan("Pro")}
                  />
                  <Label htmlFor="plan-pro" className="text-sm">
                    Pro
                  </Label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleAdd}
                className="text-xs gap-1.5"
                disabled={loading}
              >
                <Plus className="h-3.5 w-3.5" /> Create Code
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(false)}
                className="text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
            className="w-full gap-1.5 text-xs"
            disabled={loading}
          >
            <Plus className="h-3.5 w-3.5" /> Add Discount Code
          </Button>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Delete Discount</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              Are you sure you want to delete the discount code{" "}
              <span className="font-semibold">{confirmTarget?.code}</span>? This
              action cannot be undone.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!confirmTarget) return;
                await removeDiscountCode(
                  confirmTarget.id || confirmTarget.discountId,
                );
                toast({
                  title: "Code removed",
                  description: `"${confirmTarget.code}" has been deleted.`,
                });
                setConfirmOpen(false);
                setConfirmTarget(null);
              }}
              disabled={loading}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminDiscountPanel;
